// Account / profile — ported from Luan's #app-account (tab-pills + settings-card panels), bound to
// our real membership identity (non-PHI). Profile fields are READ-ONLY (D10 — identity changes go
// through the care team), styled as Luan's field-inputs. Insurance shows the carrier/member ref on
// file. Plan & billing summarizes the membership and links to the Stripe-backed Billing page — it
// never captures card data in-app (D8). Pharmacy lets the member pick their preferred pharmacy from a
// static seed list (no directory lookup) and persists it (PHI_ADMIN, demographic — see lib/pharmacy).
// The profile photo is membership data, resized client-side before upload. No clinical data.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Brand, Modal } from "@championcare/ui";
import { useApi } from "../api";
import { useAppAuth } from "../auth";
import { API_ORIGIN } from "../config";
import { useAsync } from "../lib/useAsync";
import { useBillingApi } from "../lib/billingApi";
import { tierLabel, formatDateTime, formatMoney } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import { PHARMACIES, usePharmacyApi, type Pharmacy, type SavedPharmacy, type DeliveryPref } from "../lib/pharmacy";

type Tab = "profile" | "insurance" | "plan" | "pharmacy";

// Member-editable subset of own contact identity (name, email, dob, phone). Sent to PUT
// /v1/patient/me. Plan/insurance fields (tier, member ref) are care-team-administered, not here.
type PatientProfileUpdate = {
  first_name: string;
  last_name: string;
  email: string;
  dob?: string;
  phone?: string;
};

const EMPTY_PROFILE: PatientProfileUpdate = {
  first_name: "",
  last_name: "",
  email: "",
  dob: "",
  phone: "",
};

const EditIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

// One personal-info field. View mode renders the value as a locked (disabled) input; edit mode
// binds it live to the working form. Mirrors the doctor Profile Field for a consistent look.
function Field({
  label,
  value,
  editing,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  editing: boolean;
  onChange?: (v: string) => void;
  type?: string;
}): ReactNode {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input
        className="field-input"
        type={type}
        value={value}
        readOnly={!editing || !onChange}
        disabled={!editing || !onChange}
        onChange={editing && onChange ? (e) => onChange(e.target.value) : undefined}
      />
    </div>
  );
}

// Downscale to a square ≤256px jpeg data: URL — comfortably inside the API's 400KB encoded bound.
async function fileToAvatarDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const size = Math.min(side, 256);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

const TIER_PRICE: Record<string, string> = { basic: "$50", premium: "$100", fighter: "$0" };
const TIER_DESC: Record<string, string> = {
  basic: "On-demand telehealth visits with unlimited secure messaging.",
  premium: "Priority telehealth visits, unlimited messaging, and your care team.",
  fighter: "Invitation-only membership with concierge access.",
};

// Plan & billing — relocated from the former standalone /billing page so membership + payment live on
// the Plan tab. The Stripe portal hand-off (openPortal → api.billingPortal) is moved VERBATIM; no
// behaviour change (FB is wiring real Stripe into "Manage membership"). Display-only card data (D8).
function PlanBillingPanel() {
  const api = useApi();
  const billingApi = useBillingApi();
  const { data, loading, error, reload } = useAsync(() => api.billing(), []);
  const [busy, setBusy] = useState(false);
  const [planMsg, setPlanMsg] = useState<string | null>(null);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Billing unavailable." />;

  const tier = data.tier ?? null;
  const tierName = tier ? tierLabel(tier) : "—";
  const pm = data.payment_method; // display-only brand/last4 — never a PAN (D8); null = none on file
  const pmExpiry = pm?.exp_month && pm?.exp_year ? ` · expires ${pm.exp_month}/${pm.exp_year}` : "";

  // Demo-only mock plan switch: persist the chosen tier to patients.tier, then refresh the summary.
  async function changePlan(next: "basic" | "premium") {
    if (next === tier) return;
    setBusy(true);
    setPlanMsg(null);
    try {
      await billingApi.selectPlan(next);
      setPlanMsg(`You're now on the ${tierLabel(next)} plan.`);
      reload();
    } catch (e) {
      setPlanMsg(e instanceof Error ? e.message : "Could not change your plan.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="settings-card">
        <h3>Current plan</h3>
        <p>
          Status: {data.status ?? "—"}
          {data.subscription?.current_period_end ? ` · Renews ${formatDateTime(data.subscription.current_period_end)}` : ""}.
          Your membership includes unlimited visits — there are no per-visit charges.
        </p>
        <div className="plan-summary-card" style={{ marginTop: 8 }}>
          <h4>Your plan</h4>
          <div className="plan-summary-card-name">{tierName}</div>
          <p className="plan-summary-card-desc">{tier ? TIER_DESC[tier] : "Membership details."}</p>
          {tier && (
            <div className="plan-summary-card-price">
              <span className="amt">{TIER_PRICE[tier] ?? "—"}</span>
              <span className="per">/month</span>
            </div>
          )}
          <div className="plan-change">
            {(["basic", "premium"] as const).map((t) => {
              const current = tier === t;
              return (
                <button
                  key={t}
                  type="button"
                  className={`btn ${current ? "btn-ghost" : "btn-primary"} plan-change-btn`}
                  disabled={busy || current}
                  onClick={() => changePlan(t)}
                >
                  {current
                    ? `Current plan — ${tierLabel(t)} ${TIER_PRICE[t]}/mo`
                    : busy
                      ? "Saving…"
                      : `Switch to ${tierLabel(t)} — ${TIER_PRICE[t]}/mo`}
                </button>
              );
            })}
          </div>
          {planMsg && <div className="plan-change-msg" role="status">{planMsg}</div>}
        </div>
      </div>

      <div className="settings-card">
        <h3>Payment method</h3>
        <div className="settings-row">
          <div className="settings-row-text">
            <div className="settings-row-title">{pm ? <span style={{ textTransform: "capitalize" }}>{pm.brand} ending in {pm.last4}</span> : "Card on file"}</div>
            <div className="settings-row-desc">Demo card on file{pmExpiry} — no charge.</div>
          </div>
        </div>
      </div>

      <div className="settings-card">
        <h3>Payment history</h3>
        {data.payments.length ? (
          data.payments.map((p, i) => (
            <div className="settings-row" key={i}>
              <div className="settings-row-text">
                <div className="settings-row-title">{formatMoney(p.amount_cents, p.currency)}</div>
                <div className="settings-row-desc">{formatDateTime(p.created_at)}</div>
              </div>
              <span className="visit-status completed">{p.status}</span>
            </div>
          ))
        ) : (
          <div className="pt-empty">No payments yet.</div>
        )}
      </div>
    </>
  );
}

const PillIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="3" width="12" height="18" rx="2" /><path d="M6 11h12" /><path d="M10 6h4" /></svg>
);
const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
);

// Preferred pharmacy — current-pharmacy card + a "find a different pharmacy" dialog (static list,
// client-side substring search) + a Pickup/Delivery toggle. Persists via PUT /v1/patient/pharmacy;
// reads the saved value from /me on load. Mirrors Luan's #app-account pharmacy panel.
function PharmacyPanel() {
  const pharmacyApi = usePharmacyApi();
  const [saved, setSaved] = useState<SavedPharmacy | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    pharmacyApi
      .loadSaved()
      .then((s) => active && setSaved(s))
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [pharmacyApi]);

  async function persist(next: SavedPharmacy) {
    setBusy(true);
    setError(null);
    try {
      const result = await pharmacyApi.save(next);
      setSaved(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t save your pharmacy.");
    } finally {
      setBusy(false);
    }
  }

  function choosePharmacy(p: Pharmacy) {
    setPickOpen(false);
    setQuery("");
    setSelectedId(null);
    void persist({
      id: p.id,
      name: p.name,
      address: p.address,
      phone: p.phone,
      hours: p.hours,
      delivery_pref: saved?.delivery_pref ?? "pickup",
    });
  }

  function setDelivery(pref: DeliveryPref) {
    if (!saved || saved.delivery_pref === pref) return;
    void persist({ ...saved, delivery_pref: pref });
  }

  const q = query.toLowerCase().trim();
  const filtered = q
    ? PHARMACIES.filter((p) => `${p.name} ${p.address}`.toLowerCase().includes(q))
    : PHARMACIES;

  if (loading) return <Loading />;

  return (
    <div className="settings-card">
      <h3>Preferred pharmacy</h3>
      <p>Where your prescriptions get sent at the end of a visit.</p>

      <div className="ph-current-card">
        <div className="ph-current-icon">{PillIcon}</div>
        <div className="ph-current-info">
          {saved ? (
            <>
              <div className="ph-current-name">{saved.name}</div>
              <div className="ph-current-addr">{saved.address}</div>
              <div className="ph-current-meta">
                {saved.phone && <span>{saved.phone}</span>}
                {saved.phone && saved.hours && <span className="sep">·</span>}
                {saved.hours && <span>{saved.hours}</span>}
              </div>
            </>
          ) : (
            <>
              <div className="ph-current-name">No pharmacy on file</div>
              <div className="ph-current-addr">Choose a pharmacy to receive prescriptions.</div>
            </>
          )}
        </div>
      </div>

      <div className="ph-actions">
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => setPickOpen(true)}>
          <span style={{ width: 14, height: 14, display: "inline-flex" }}>{SearchIcon}</span>
          {saved ? "Find a different pharmacy" : "Add a pharmacy"}
        </button>
      </div>
      {error && <ErrorState message={error} />}

      {saved && (
        <>
          <div className="ph-pref-label">Delivery preference</div>
          <div className="ph-pref">
            <button
              className={`ph-pref-pill ${saved.delivery_pref === "pickup" ? "active" : ""}`}
              disabled={busy}
              onClick={() => setDelivery("pickup")}
            >
              Pickup
            </button>
            <button
              className={`ph-pref-pill ${saved.delivery_pref === "delivery" ? "active" : ""}`}
              disabled={busy}
              onClick={() => setDelivery("delivery")}
            >
              Delivery
            </button>
          </div>
        </>
      )}

      <Modal open={pickOpen} onClose={() => setPickOpen(false)} labelledBy="phPickTitle">
        <div className="dialog-eyebrow">Pharmacy</div>
        <div className="dialog-title" id="phPickTitle">Find a <em>pharmacy</em></div>
        <div className="ph-search-wrap">
          <span className="ph-search-icon">{SearchIcon}</span>
          <input
            className="ph-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or address…"
            aria-label="Search pharmacies"
          />
        </div>
        <div className="ph-list">
          {filtered.length ? (
            filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`ph-card ${selectedId === p.id ? "selected" : ""}`}
                onClick={() => choosePharmacy(p)}
                onMouseEnter={() => setSelectedId(p.id)}
              >
                <div className="ph-card-icon">{PillIcon}</div>
                <div className="ph-card-info">
                  <div className="ph-card-name">{p.name}</div>
                  <div className="ph-card-addr">{p.address}</div>
                  <div className="ph-card-meta">
                    {p.distance && <span className="dist">{p.distance}</span>}
                    <span>{p.hours}</span>
                  </div>
                </div>
              </button>
            ))
          ) : (
            <div className="ph-empty">No pharmacies match “{query}”.</div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export function Account() {
  const api = useApi();
  const { getToken, updateProfile } = useAppAuth();
  const { data, loading, error } = useAsync(() => api.me(), []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const initialTab = (["profile", "insurance", "plan"].includes(searchParams.get("tab") ?? "")
    ? (searchParams.get("tab") as Tab)
    : "profile");
  const [tab, setTab] = useState<Tab>(initialTab);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Personal-info edit state — `savedForm` is the read-only source of truth; `form` is the working
  // copy edited in edit mode. Cancel resets form ← savedForm. Mirrors the doctor Profile page.
  const [savedForm, setSavedForm] = useState<PatientProfileUpdate>(EMPTY_PROFILE);
  const [form, setForm] = useState<PatientProfileUpdate>(EMPTY_PROFILE);
  const [editing, setEditing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const next: PatientProfileUpdate = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      dob: data.dob?.slice(0, 10) ?? "",
      phone: data.phone ?? "",
    };
    setSavedForm(next);
    setForm(next);
  }, [data]);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Profile unavailable." />;

  // Name displays read from `savedForm` (set instantly on save; loaded from `data`) so the photo
  // header / member card / initials refresh on Save without a page reload. Fall back to `data` for
  // the first render, before the load effect populates savedForm.
  const firstName = savedForm.first_name || data.first_name;
  const lastName = savedForm.last_name || data.last_name;
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const avatarUrl = photoUrl ?? data.avatar_url ?? null;

  async function onPhotoPicked(file: File | undefined) {
    if (!file) return;
    setPhotoBusy(true);
    setPhotoError(false);
    try {
      const image = await fileToAvatarDataUrl(file);
      const { avatar_url } = await api.uploadAvatar(image);
      setPhotoUrl(avatar_url);
    } catch {
      setPhotoError(true);
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function setField<K extends keyof PatientProfileUpdate>(key: K, value: PatientProfileUpdate[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function startEdit() {
    setForm(savedForm);
    setProfileError(null);
    setProfileSaved(false);
    setEditing(true);
  }

  function cancelEdit() {
    setForm(savedForm);
    setProfileError(null);
    setEditing(false);
  }

  async function saveProfile() {
    setSavingProfile(true);
    setProfileError(null);
    try {
      // Direct call to PUT /v1/patient/me — auth + envelope handling mirror the typed SDK client
      // (bearer token from getToken, credentials: "include", { data } / { error } response shape).
      const body: PatientProfileUpdate = {
        ...form,
        dob: form.dob || undefined,
        phone: form.phone || undefined,
      };
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const token = getToken ? await getToken() : null;
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}/v1/patient/me`, {
        method: "PUT",
        headers,
        credentials: "include",
        body: JSON.stringify(body),
      });
      const text = await res.text();
      const json = text ? (JSON.parse(text) as { error?: { message?: string } }) : {};
      if (!res.ok) throw new Error(json.error?.message ?? "Request failed");
      // Mirror the name into the auth identity when the provider supports it (no-op on Cognito —
      // the SPA client can't write name attributes — and in dev). The backend PUT already
      // succeeded, so an auth-provider hiccup must not fail the save.
      try {
        await updateProfile?.({ firstName: form.first_name, lastName: form.last_name });
      } catch {
        /* the backend remains the name source of truth */
      }
      setSavedForm(form);
      setEditing(false);
      setProfileSaved(true);
    } catch (e) {
      setProfileError(e instanceof Error ? e.message : "We couldn’t save your changes. Please try again.");
    } finally {
      setSavingProfile(false);
    }
  }

  // Read-only view renders from the last-saved values; edit view renders the working form.
  const pview = editing ? form : savedForm;

  return (
    <>
      <div className="app-page-head">
        <h1>Profile <em>&amp; billing.</em></h1>
        <p>Manage your personal info, insurance, and plan.</p>
      </div>

      <div className="tab-pills" role="tablist">
        <button className={`tab-pill ${tab === "profile" ? "active" : ""}`} onClick={() => setTab("profile")} role="tab">Profile</button>
        <button className={`tab-pill ${tab === "insurance" ? "active" : ""}`} onClick={() => setTab("insurance")} role="tab">Insurance</button>
        <button className={`tab-pill ${tab === "pharmacy" ? "active" : ""}`} onClick={() => setTab("pharmacy")} role="tab">Pharmacy</button>
        <button className={`tab-pill ${tab === "plan" ? "active" : ""}`} onClick={() => setTab("plan")} role="tab">Plan &amp; billing</button>
      </div>

      {tab === "profile" && (
        <>
          <div className="settings-card">
            <div className="pt-account__head">
              <div className="pt-account__avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} />
                ) : (
                  initials
                )}
              </div>
              <div>
                <div className="pt-account__name">{firstName} {lastName}</div>
                <div className="pt-account__email">{data.email}</div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => onPhotoPicked(e.target.files?.[0])} />
                <button className="btn btn-ghost btn-sm" disabled={photoBusy} onClick={() => fileRef.current?.click()} style={{ marginTop: 10 }}>
                  {photoBusy ? "Uploading…" : "Change photo"}
                </button>
                {photoError && (
                  <p className="settings-row-desc" role="alert" style={{ marginTop: 6 }}>
                    We couldn&rsquo;t update your photo — please try a smaller image.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="pcard-head">
              <h3>Personal <em>information</em></h3>
              {!editing && (
                <button className="pbtn pbtn-ghost pbtn-sm" onClick={startEdit}>
                  {EditIcon} Edit
                </button>
              )}
            </div>
            <p>This is the info your doctor sees before each visit. Membership and member ID are managed by your care team.</p>
            <div className="field-row">
              <Field label="First name" value={pview.first_name} editing={editing} onChange={(v) => setField("first_name", v)} />
              <Field label="Last name" value={pview.last_name} editing={editing} onChange={(v) => setField("last_name", v)} />
            </div>
            <div className="field-row">
              <Field label="Email" type="email" value={pview.email} editing={editing} onChange={(v) => setField("email", v)} />
              <Field label="Phone" type="tel" value={pview.phone ?? ""} editing={editing} onChange={(v) => setField("phone", v)} />
            </div>
            <div className="field-row">
              <Field label="Date of birth" type="date" value={pview.dob ?? ""} editing={editing} onChange={(v) => setField("dob", v)} />
              <Field label="Membership" value={tierLabel(data.tier)} editing={false} />
            </div>
            <div className="field-row">
              <Field label="Member ID" value={data.insurance_member_ref ?? "—"} editing={false} />
              <div className="field" />
            </div>

            {(editing || profileSaved || profileError) && (
              <div className="form-actions">
                {profileError && <span className="save-error" role="alert">{profileError}</span>}
                {profileSaved && !editing && <span className="saved-note">Saved.</span>}
                {editing && (
                  <>
                    <button className="pbtn pbtn-ghost" disabled={savingProfile} onClick={cancelEdit}>Cancel</button>
                    <button className="pbtn pbtn-primary" disabled={savingProfile} onClick={saveProfile}>
                      {savingProfile ? "Saving…" : "Save changes"}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "insurance" && (
        <div className="ins-tab-grid">
          {/* White container is ONLY for "Insurance on file" */}
          <div className="settings-card">
            <h3>Insurance on file</h3>
            <p>Used for prescriptions, lab orders, and specialist referrals. To update it, message your care team.</p>
            <div className="ins-current-card">
              <div className="ins-current-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L4 6v6c0 5 3.5 9.5 8 10 4.5-0.5 8-5 8-10V6l-8-4z" /><polyline points="9 12 11 14 15 10" /></svg>
              </div>
              <div className="ins-current-info">
                <div className="ins-current-top">
                  <div className="ins-current-name">{data.insurance_carrier ?? "No carrier on file"}</div>
                  <span className="ins-plan-pill">{tierLabel(data.tier)}</span>
                </div>
                <div className="ins-current-meta">
                  <span className="lbl">Member ID</span> <span>{data.insurance_member_ref ?? "—"}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Member card — standalone (no white container), hugging the right. */}
          <div className="ins-tab-right">
            <div className="pt-mcard">
              <div className="pt-mcard__top">
                <Brand size={20} />
                <span className="pt-mcard__tier">{tierLabel(data.tier)} membership</span>
              </div>
              <div className="pt-mcard__name">
                {firstName} {lastName}
              </div>
              <div className="pt-mcard__grid">
                <div>
                  <span className="pt-mcard__k">Member ID</span>
                  <span className="pt-mcard__v mono">{data.insurance_member_ref ?? "—"}</span>
                </div>
                <div>
                  <span className="pt-mcard__k">Carrier</span>
                  <span className="pt-mcard__v">{data.insurance_carrier ?? "Champion Care"}</span>
                </div>
              </div>
            </div>
            <p className="pt-note">You&rsquo;ll be able to add this to Apple/Google Wallet in the mobile app.</p>
          </div>
        </div>
      )}

      {tab === "pharmacy" && <PharmacyPanel />}

      {tab === "plan" && <PlanBillingPanel />}
    </>
  );
}
